import type { ScheduledMatch, SportsMatch } from '../account/api';

/**
 * The coming days on Home (#93): a day picker over 7 days, each day's
 * matches (today's with channels, the relay's schedule for the rest), a line
 * per ranked league that doesn't play that day, and the coming days' big
 * matches for the slider when today has none. Days are the viewer's own
 * (local time).
 */

/** Days in the day picker: today and the six after it. */
export const DAYS = 7;
const DAY_MS = 24 * 3600_000;
/** A coming match worth the slider (a major league, or the viewer's own clubs). */
export const BIG_MATCH_SCORE = 60;
/** Slides of coming matches. */
const UPCOMING_SLIDES = 5;

/** Local midnight of the day `at` falls in. */
export function dayStart(at: number): number {
  const date = new Date(at);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/** The start of the next local day (not always 24 h later: summer time). */
export function nextDay(start: number): number {
  return dayStart(start + DAY_MS + 3 * 3600_000);
}

/** The picker's days, as local midnights, today first. */
export function pickerDays(now: number, count = DAYS): number[] {
  const days = [dayStart(now)];
  while (days.length < count) days.push(nextDay(days[days.length - 1]!));
  return days;
}

/** A schedule entry shown like a match: no channels before match day, only those it is expected on (#111). */
export function asListed(match: ScheduledMatch): SportsMatch {
  return {
    id: match.id,
    home: match.home,
    away: match.away,
    competition: match.competition,
    league: match.league,
    leagueRank: match.leagueRank,
    startsAt: match.startsAt,
    live: false,
    checking: false,
    homeCrest: match.homeCrest,
    awayCrest: match.awayCrest,
    liveScore: null,
    channels: [],
    ...(match.upcoming && match.upcoming.length > 0 ? { upcoming: match.upcoming } : {}),
    scheduled: true,
  };
}

/** Folded words of a team name, without club words ("FC Barcelona" → ["barcelona"]). */
function words(name: string): string[] {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length >= 3 && !['fc', 'cf', 'afc', 'club', 'the', 'women'].includes(word));
}

/** Loosely the same team: a word in common, or one a short form of the other ("Man Utd" / "Manchester United"). */
function sameTeam(a: string, b: string): boolean {
  const left = words(a);
  const right = words(b);
  return left.some((x) => right.some((y) => x === y || (x.length >= 3 && y.startsWith(x)) || (y.length >= 3 && x.startsWith(y))));
}

/** A schedule entry that is already in today's list (FotMob's names can differ from the provider's). */
export function sameFixture(listed: Pick<SportsMatch, 'home' | 'away' | 'startsAt' | 'id'>, scheduled: Pick<ScheduledMatch, 'home' | 'away' | 'startsAt' | 'id'>): boolean {
  if (listed.id === scheduled.id) return true;
  if (listed.startsAt !== null && Math.abs(listed.startsAt - scheduled.startsAt) > 3 * 3600_000) return false;
  return (sameTeam(listed.home, scheduled.home) && sameTeam(listed.away, scheduled.away)) || (sameTeam(listed.home, scheduled.away) && sameTeam(listed.away, scheduled.home));
}

/**
 * One day's matches: today's list (with channels) for the matches that fall
 * on it, plus the schedule's matches of that day that the list doesn't have.
 * Live matches and those without a time belong to today.
 */
export function matchesOnDay(listed: SportsMatch[], schedule: ScheduledMatch[], day: number, now: number): SportsMatch[] {
  const end = nextDay(day);
  const today = dayStart(now);
  const onDay = (at: number | null) => (at === null ? day === today : at >= day && at < end);
  const own = listed.filter((match) => (match.live ? day === today : onDay(match.startsAt)));
  const extra = schedule.filter((match) => match.startsAt > now && onDay(match.startsAt) && !listed.some((known) => sameFixture(known, match)));
  return [...own, ...extra.map(asListed)];
}

/** The api's bar for a match worth the slider (`SPORTS_FEATURED_MIN_SCORE`). */
export const FEATURED_MIN_SCORE = 45;

/**
 * The slider's matches that are on today in the viewer's own time: live, or
 * kicking off before local midnight. The relay's "today" runs 18 hours ahead,
 * so in the evening it already holds tomorrow morning's games.
 */
export function featuredToday(featured: SportsMatch[], now: number): SportsMatch[] {
  const end = nextDay(dayStart(now));
  return featured.filter((match) => match.live || match.startsAt === null || match.startsAt < end);
}

/**
 * Whether today (local) has a match worth the slider. The api answers for its own
 * 18-hour window; with scores (newer servers) the app checks today's matches itself.
 */
export function bigMatchToday(featured: SportsMatch[], bigToday: boolean | undefined, now: number): boolean {
  if (bigToday === false) return false;
  const todays = featuredToday(featured, now);
  if (todays.length === 0) return false;
  if (todays.some((match) => typeof match.score !== 'number')) return true;
  return todays.some((match) => match.score! >= FEATURED_MIN_SCORE);
}

export interface LeagueLine {
  league: string;
  rank: number;
  /** Local midnight of the league's next match day (to jump to it). */
  nextDay: number;
  /** Its first match that day. */
  next: number;
}

/**
 * A line for each ranked league that doesn't play on the chosen day but
 * plays later in the schedule ("Premier League: no match today, next Sat 1 Nov").
 * Leagues not in the schedule at all get none.
 */
export function leagueLines(dayMatches: SportsMatch[], schedule: ScheduledMatch[], day: number, favourites: readonly string[] = []): LeagueLine[] {
  const playing = new Set(dayMatches.map((match) => match.league ?? match.competition));
  const end = nextDay(day);
  const lines = new Map<string, LeagueLine>();
  for (const match of schedule) {
    if (playing.has(match.league) || match.startsAt < end) continue;
    const known = lines.get(match.league);
    if (!known || match.startsAt < known.next) lines.set(match.league, { league: match.league, rank: match.leagueRank, next: match.startsAt, nextDay: dayStart(match.startsAt) });
  }
  const favourite = (line: LeagueLine) => (favourites.includes(line.league) ? 0 : 1);
  return [...lines.values()].sort((a, b) => favourite(a) - favourite(b) || a.rank - b.rank);
}

/**
 * The slider's matches when today has no big one: the coming days' biggest
 * (for this viewer), from the first day that has one, in kick-off order.
 */
export function upcomingHighlights(schedule: ScheduledMatch[], now: number, count = UPCOMING_SLIDES): SportsMatch[] {
  const later = schedule.filter((match) => match.startsAt >= nextDay(dayStart(now)) && match.score >= BIG_MATCH_SCORE).sort((a, b) => a.startsAt - b.startsAt);
  const first = later[0];
  if (!first) return [];
  // Two days from the first big match: next weekend's games, not those a week away.
  const window = later.filter((match) => match.startsAt < first.startsAt + 2 * DAY_MS);
  return [...window]
    .sort((a, b) => b.score - a.score || a.startsAt - b.startsAt)
    .slice(0, count)
    .sort((a, b) => a.startsAt - b.startsAt)
    .map(asListed);
}
